import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Upload, Link as LinkIcon, X } from 'lucide-react';
import { MAX_IMAGE_BYTES } from './adminApi';

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

const ImageEditor = ({ images, onChange }) => {
  const [url, setUrl] = useState('');
  const fileRef = useRef(null);

  const addUrl = () => {
    const v = url.trim();
    if (!v) return;
    onChange([...images, v]);
    setUrl('');
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    const next = [...images];
    for (const file of files) {
      if (file.size > MAX_IMAGE_BYTES) {
        toast.error(`${file.name} is larger than 4MB and was skipped`);
        continue;
      }
      // eslint-disable-next-line no-await-in-loop
      next.push(await readAsDataUrl(file));
    }
    onChange(next);
    if (fileRef.current) fileRef.current.value = '';
  };

  const removeAt = (idx) => onChange(images.filter((_, i) => i !== idx));

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= images.length) return;
    const copy = [...images];
    [copy[idx], copy[j]] = [copy[j], copy[idx]];
    onChange(copy);
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="flex-1 flex gap-2">
          <LinkIcon className="w-4 h-4 text-gray-400 mt-3 shrink-0" />
          <Input
            data-testid="image-url-input"
            placeholder="Paste image URL and press Add"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addUrl(); } }}
            className="bg-slate-800 border-slate-700 text-white"
          />
          <Button type="button" data-testid="image-url-add" onClick={addUrl} variant="outline" className="border-slate-700 text-white hover:bg-slate-800">
            Add
          </Button>
        </div>
      </div>
      <div>
        <input
          ref={fileRef}
          data-testid="image-file-input"
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
        />
        <Button
          type="button"
          data-testid="image-file-button"
          variant="outline"
          onClick={() => fileRef.current?.click()}
          className="border-slate-700 text-white hover:bg-slate-800"
        >
          <Upload className="w-4 h-4 mr-2" /> Upload from device
        </Button>
        <span className="text-xs text-gray-500 ml-3">Max 4MB per image</span>
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {images.map((src, idx) => (
            <div key={src} className="relative group bg-slate-800 rounded border border-slate-700 overflow-hidden">
              <img src={src} alt={`preview ${idx}`} className="w-full h-24 object-cover" />
              <button
                type="button"
                data-testid={`image-remove-${idx}`}
                onClick={() => removeAt(idx)}
                className="absolute top-1 right-1 bg-black/70 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100"
              >
                <X className="w-3 h-3" />
              </button>
              <div className="absolute bottom-1 left-1 flex gap-1 opacity-0 group-hover:opacity-100">
                <button type="button" onClick={() => move(idx, -1)} className="bg-black/70 text-white text-xs px-1 rounded">←</button>
                <button type="button" onClick={() => move(idx, 1)} className="bg-black/70 text-white text-xs px-1 rounded">→</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageEditor;
